import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard as CreditCardIcon, MoreHorizontal, Plus } from "lucide-react";

import { CreditCardFormDialog } from "@/components/credit-cards/credit-card-form-dialog";
import { DeleteCreditCardDialog } from "@/components/credit-cards/delete-credit-card-dialog";
import { UtilizationBar } from "@/components/credit-cards/utilization-bar";
import { LastUpdated } from "@/components/data/last-updated";
import { RefreshButton } from "@/components/data/refresh-button";
import { SourceBadge } from "@/components/data/source-badge";
import { SyncStatusPill } from "@/components/data/sync-status-pill";
import { PageHeader } from "@/components/page-header";
import { PlaidEmptyStateCta } from "@/components/settings/plaid-empty-state-cta";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listCreditCards } from "@/lib/api";
import { formatAbsoluteDateTime, formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CreditCard } from "@finance/shared";

interface PageState {
  status: "loading" | "ready" | "error";
  cards: CreditCard[];
  error: string | null;
}

const INITIAL: PageState = {
  status: "loading",
  cards: [],
  error: null,
};

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function formatDueDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  return dateFormatter.format(new Date(ts));
}

export function CreditCardsPage() {
  const [state, setState] = useState<PageState>(INITIAL);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CreditCard | null>(null);
  const [deleting, setDeleting] = useState<CreditCard | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    setState((prev) =>
      prev.status === "ready"
        ? { ...prev, status: "ready", error: null }
        : INITIAL,
    );
    return listCreditCards(signal)
      .then((cards) => {
        if (signal?.aborted) return;
        setState({ status: "ready", cards, error: null });
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        setState({
          status: "error",
          cards: [],
          error:
            err instanceof Error ? err.message : "Failed to load credit cards",
        });
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const rows = useMemo(() => {
    return [...state.cards].sort((a, b) =>
      a.last_updated_at < b.last_updated_at
        ? 1
        : a.last_updated_at > b.last_updated_at
          ? -1
          : 0,
    );
  }, [state.cards]);

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(card: CreditCard) {
    setEditing(card);
    setFormOpen(true);
  }

  function openDelete(card: CreditCard) {
    setDeleting(card);
  }

  function afterMutation() {
    void load();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Credit cards"
        title="Credit cards"
        description="Manual credit card balances, limits, and utilization. Plaid sync is in a later issue."
        actions={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" aria-hidden />
            Add card
          </Button>
        }
      />

      {state.status === "loading" ? (
        <CreditCardsSkeleton />
      ) : state.status === "error" ? (
        <ErrorCard message={state.error ?? "Failed to load credit cards"} />
      ) : rows.length === 0 ? (
        <EmptyState onAdd={openAdd} />
      ) : (
        <CreditCardsTable
          rows={rows}
          onEdit={openEdit}
          onDelete={openDelete}
          onRefreshed={afterMutation}
        />
      )}

      <CreditCardFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        card={editing}
        onSaved={afterMutation}
      />
      <DeleteCreditCardDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        card={deleting}
        onDeleted={afterMutation}
      />
    </div>
  );
}

interface CreditCardsTableProps {
  rows: CreditCard[];
  onEdit: (card: CreditCard) => void;
  onDelete: (card: CreditCard) => void;
  onRefreshed: () => void;
}

function CreditCardsTable({
  rows,
  onEdit,
  onDelete,
  onRefreshed,
}: CreditCardsTableProps) {
  const navigate = useNavigate();
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Issuer</TableHead>
            <TableHead className="text-right">Balance owed</TableHead>
            <TableHead className="text-right">Credit limit</TableHead>
            <TableHead className="text-right">Available</TableHead>
            <TableHead>Utilization</TableHead>
            <TableHead>Due date</TableHead>
            <TableHead className="text-right">Min payment</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Sync</TableHead>
            <TableHead>Last updated</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const open = () =>
              navigate(`/credit-cards/${row.id}/transactions`);
            return (
            <TableRow
              key={row.id}
              role="link"
              tabIndex={0}
              aria-label={`Open transactions for ${row.name}`}
              className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0"
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  open();
                }
              }}
            >
              <TableCell className="font-medium text-foreground">
                {row.name}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.issuer}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {formatCurrency(row.current_balance_owed)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {formatCurrency(row.credit_limit)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {formatCurrency(row.available_credit)}
              </TableCell>
              <TableCell>
                <UtilizationBar value={row.utilization_pct} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.due_date ? (
                  <span title={formatAbsoluteDateTime(row.due_date)}>
                    {formatDueDate(row.due_date)}
                  </span>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {row.minimum_payment != null
                  ? formatCurrency(row.minimum_payment)
                  : "—"}
              </TableCell>
              <TableCell>
                <SourceBadge source={row.source} />
              </TableCell>
              <TableCell>
                <SyncStatusPill
                  source={row.source}
                  lastSyncedAt={row.last_synced_at}
                  lastSyncStatus={row.last_sync_status}
                />
              </TableCell>
              <TableCell>
                <LastUpdated iso={row.last_updated_at} />
              </TableCell>
              <TableCell className="text-right">
                {/* Stop propagation so action-cell clicks don't trigger
                    the row-level navigation to the transactions page. */}
                <div
                  className="flex items-center justify-end gap-1"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {row.source === "api" ? (
                    <RefreshButton
                      resource="credit-cards"
                      id={row.id}
                      label={row.name}
                      onRefreshed={onRefreshed}
                    />
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Actions for ${row.name}`}
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEdit(row)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => onDelete(row)}
                        className="text-destructive focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function CreditCardsSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-10 rounded-md bg-muted/60 animate-pulse",
              i === 3 && "w-2/3",
            )}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface EmptyStateProps {
  onAdd: () => void;
}

function EmptyState({ onAdd }: EmptyStateProps) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
          <CreditCardIcon className="h-5 w-5" aria-hidden />
        </div>
        <p className="text-sm font-medium text-foreground">
          No credit cards yet.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Add a card to track balance owed, credit limit, and utilization — or
          connect your card issuer with Plaid to sync balances automatically.
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <Button onClick={onAdd}>
            <Plus className="h-4 w-4" aria-hidden />
            Add card
          </Button>
          <PlaidEmptyStateCta />
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-destructive"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Couldn't load credit cards
          </p>
          <p className="font-mono text-xs text-muted-foreground">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
